import { useEffect, type RefObject } from 'react'

/** Höchste Höhe der Verläufe in px — so hoch wie die Breadcrumb-Zeile. */
export const SCROLL_FADE_MAX = 40

/**
 * Die **Verläufe oben und unten** einer rollenden Liste (Projekt, Kurz notiert,
 * Geschafft, Ruhend, Wiederkehrend): schreibt `--fade-top-height` und `--fade-bottom-height` an
 * den Rahmen um den Scroller. Gezeichnet werden sie vom Mixin
 * `scroll-fades` (`styles/_scroll-fades.scss`).
 *
 * Die HÖHE folgt der Scrollstrecke, nicht die Deckkraft. Eine Deckkraft-Rampe
 * schwächte den ganzen Verlauf ab, auch die obersten Pixel, die an der
 * Schnittkante voll deckend sein müssen — bei wenig Scroll verdeckte er den
 * Schnitt nicht und war trotzdem zu sehen. Wächst stattdessen die Höhe von 0
 * auf 40px, bleibt der Verlauf an der Kante deckend und blendet stufenlos ein.
 *
 * Als CSS-Variable statt React-State: Das läuft bei jedem Scroll-Ereignis und
 * würde sonst die ganze Seite neu rendern.
 *
 * @param scrollerRef Das Element, das rollt.
 * @param frameRef Der Rahmen darum, der die Verläufe trägt — NICHT der Scroller
 *   selbst: Darin klemmte sie sein Inhaltskasten, und sie liefen mit.
 */
export function useScrollFades(
  scrollerRef: RefObject<HTMLElement | null>,
  frameRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const scroller = scrollerRef.current
    const frame = frameRef.current
    if (!scroller || !frame) return

    const update = () => {
      // Ein Scroller, der gerade nicht rollt (Klären in Kurz notiert: `overflow: hidden`), hat
      // keine Verläufe. Dort tauscht der Inhalt ohne Größenänderung und ohne Neu-Rendern der
      // Seite — gemessen am Überstand blieb das untere Band über der Klären-Ansicht stehen.
      const overflowY = getComputedStyle(scroller).overflowY
      if (overflowY !== 'auto' && overflowY !== 'scroll') {
        frame.style.setProperty('--fade-top-height', '0px')
        frame.style.setProperty('--fade-bottom-height', '0px')
        return
      }
      const top = Math.min(scroller.scrollTop, SCROLL_FADE_MAX)
      const remaining = scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop
      const bottom = Math.min(Math.max(remaining, 0), SCROLL_FADE_MAX)
      frame.style.setProperty('--fade-top-height', `${top}px`)
      frame.style.setProperty('--fade-bottom-height', `${bottom}px`)
    }

    update()
    scroller.addEventListener('scroll', update, { passive: true })
    // Die Inhaltshöhe ändert sich beim Anlegen und Filtern von Aufgaben.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    observer?.observe(scroller)
    return () => {
      scroller.removeEventListener('scroll', update)
      observer?.disconnect()
    }
    // BEWUSST ohne Abhängigkeitsliste: Beim Laden rendern die Seiten zuerst
    // ein Skelett, dort sind die Refs `null` und der Effekt bricht ab. Mit `[]`
    // liefe er danach nie wieder — die Verläufe blieben dauerhaft aus.
  })
}
